import { CheckCircle2, Circle, Loader2, type LucideIcon, MinusCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Text } from "@/components/Text";
import type { ResearchCounts as Counts, ResearchTopic, TopicStatus } from "./researchModel";

const STATUS_ICONS: Record<TopicStatus, LucideIcon> = {
  pending: Circle,
  active: Loader2,
  done: CheckCircle2,
  skipped: MinusCircle,
};

const STATUS_CLASSES: Record<TopicStatus, string> = {
  pending: "text-content-tertiary",
  active: "text-content-secondary motion-safe:animate-spin",
  done: "text-success",
  skipped: "text-content-tertiary",
};

/** A research run's topics, each with where it stands (its icon says it to screen readers). */
export function ResearchTopics({ topics }: Readonly<{ topics: readonly ResearchTopic[] }>) {
  const { t } = useTranslation("chat", { keyPrefix: "research.progress" });
  if (topics.length === 0) return null;
  return (
    <ul aria-label={t("topics")} className="space-y-1">
      {topics.map((topic) => {
        const Icon = STATUS_ICONS[topic.status];
        return (
          <li key={topic.id} className="flex items-start gap-2">
            <Icon
              size={14}
              role="img"
              aria-label={t(`topicStatus.${topic.status}`)}
              className={`mt-0.5 shrink-0 ${STATUS_CLASSES[topic.status]}`}
            />
            <Text variant="body-sm" tone={topic.status === "skipped" ? "tertiary" : "secondary"} className="min-w-0">
              {topic.title}
            </Text>
          </li>
        );
      })}
    </ul>
  );
}

/** How much a run searched, found and read; a finished run adds its rounds and depth. */
export function ResearchCounts({ counts, extra = [] }: Readonly<{ counts: Counts; extra?: string[] }>) {
  const { t } = useTranslation("chat", { keyPrefix: "research.progress" });
  const items = [
    t("queries", { count: counts.queries }),
    t("sources", { count: counts.sources }),
    t("pages", { count: counts.pages }),
    ...extra,
  ];
  return (
    <ul className="flex flex-wrap gap-x-3 gap-y-1">
      {items.map((item) => (
        <li key={item}>
          <Text variant="caption" tone="tertiary">
            {item}
          </Text>
        </li>
      ))}
    </ul>
  );
}
